import { lazy, Suspense } from 'react'
import { createRootRoute, createRoute, createRouter, RouterProvider, Outlet } from '@tanstack/react-router'
import { FiAlertTriangle, FiRefreshCw } from 'react-icons/fi'
import { ERROR_VALIDATION, ERROR_UNKNOWN, LOADING } from './constants/globalConstants'
import { Layout } from './components/Layout'
import { useMovieDatabaseContext } from './context/MovieDatabaseContext'
import type { Title } from './data/dataTypes'

const Homepage = lazy(async () => import('./components/Homepage/Homepage'))
const MovieOverview = lazy(async () => import('./components/MovieOverview/MovieOverview'))
const MovieDetail = lazy(async () => import('./components/MovieDetail/MovieDetail'))
const MissingTitles = lazy(async () => import('./components/MissingTitles/MissingTitles'))
const RequestPage = lazy(async () => import('./components/Request/RequestPage'))
const PageNotFound = lazy(async () => import('./components/PageNotFound'))
const Search = lazy(async () => import('./components/Search/Search'))
const RandomMovieIntro = lazy(async () => import('./components/RandomMovieIntro/randomMovieIntro'))

function PendingLoadingState() {
  return (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        height: '100vh',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <p>{LOADING}</p>
    </div>
  )
}

function ErrorLoadingState({ errorReason }: { readonly errorReason: 'validation_error' | 'unknown_error' }) {
  const message = errorReason === 'validation_error' ? ERROR_VALIDATION : ERROR_UNKNOWN
  return (
    <div className='flex flex-col items-center justify-center h-screen w-full gap-4'>
      <FiAlertTriangle size={36} className='text-orange-400 dark:text-orange-700' />
      <p>{message}</p>
      <button
        type='button'
        onClick={() => {
          window.location.reload()
        }}
        className='flex flex-row items-center gap-2 cursor-pointer bg-transparent border-none dark:text-white text-neutral-800'
      >
        <FiRefreshCw size={18} />
        <span>Probeer opnieuw</span>
      </button>
    </div>
  )
}

function useAllMovies(): ReadonlyArray<Title> {
  const context = useMovieDatabaseContext()
  return context.movieDatabaseLoadingState === 'success' ? context.allMovies : []
}

function HomepageRoute() {
  return <Homepage allMovies={useAllMovies()} />
}
function MovieOverviewRoute() {
  return <MovieOverview allMovies={useAllMovies()} />
}
function MovieDetailRoute() {
  return <MovieDetail allMovies={useAllMovies()} />
}
function MissingTitlesRoute() {
  return <MissingTitles allMovies={useAllMovies()} />
}
function RequestPageRoute() {
  return <RequestPage allMovies={useAllMovies()} />
}
function PageNotFoundRoute() {
  return <PageNotFound allMovies={useAllMovies()} />
}
function SearchRoute() {
  return <Search allMovies={useAllMovies()} />
}

const rootRoute = createRootRoute({
  component: () => (
    <Layout>
      <Suspense fallback={<PendingLoadingState />}>
        <Outlet />
      </Suspense>
    </Layout>
  ),
})

const homepageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: (search: Readonly<Record<string, unknown>>): { category?: 'film' | 'series' } => {
    return search.category === 'film' || search.category === 'series' ? { category: search.category } : {}
  },
  component: HomepageRoute,
})

const overviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/overview/$category',
  component: MovieOverviewRoute,
})

const itemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/item/$movieID',
  component: MovieDetailRoute,
})

const missingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/missing',
  component: MissingTitlesRoute,
})

const requestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/request',
  component: RequestPageRoute,
})

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/search',
  component: SearchRoute,
})

const randomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/random/$movieID',
  component: () => <RandomMovieIntro />,
})

const routeTree = rootRoute.addChildren([
  homepageRoute,
  overviewRoute,
  itemRoute,
  missingRoute,
  requestRoute,
  searchRoute,
  randomRoute,
])

export const router = createRouter({ routeTree, defaultNotFoundComponent: PageNotFoundRoute })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export function RoutesHandeler() {
  const context = useMovieDatabaseContext()

  if (context.movieDatabaseLoadingState === 'idle' || context.movieDatabaseLoadingState === 'pending') {
    return <PendingLoadingState />
  }
  if (context.movieDatabaseLoadingState === 'error') {
    return <ErrorLoadingState errorReason={context.errorReason} />
  }

  return <RouterProvider router={router} />
}
